import { useEffect, useState } from "react";
import { CheckCircle2, Download, ExternalLink, FileWarning, Terminal } from "lucide-react";
import { loadReleaseManifest } from "../lib/release";
import type { ReleaseManifest } from "../types";
import { SafetyNotice } from "./SafetyNotice";

const repositoryUrl = import.meta.env.VITE_GITHUB_REPOSITORY_URL || "https://github.com/MASHR-SYSTEM/blockr";

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KiB", "MiB", "GiB"];
  let value = bytes / 1024;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(1)} ${units[index]}`;
}

export function Downloads() {
  const [manifest, setManifest] = useState<ReleaseManifest | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    loadReleaseManifest(controller.signal).then(setManifest).catch((caught: unknown) => {
      if ((caught as Error).name !== "AbortError") setError((caught as Error).message);
    });
    return () => controller.abort();
  }, []);

  return (
    <section id="download" className="section download-section" aria-labelledby="download-title">
      <div className="section-heading">
        <span className="eyebrow">Verified releases</span>
        <h2 id="download-title">Download exactly what you intend to run.</h2>
        <p>Blockr never auto-detects or silently downloads a build. Choose a platform and verify the archive before extraction.</p>
      </div>

      <SafetyNotice compact />

      {error ? <div className="status-panel error" role="alert"><FileWarning /> {error} Downloads are disabled.</div> : null}
      {!manifest && !error ? <div className="status-panel" role="status">Checking the signed release manifest…</div> : null}
      {manifest && !manifest.available ? (
        <div className="status-panel unavailable" role="status">
          <FileWarning aria-hidden="true" />
          <div>
            <strong>No verified public binary is available yet.</strong>
            <p>{manifest.reason} The button will remain disabled rather than serve an unverified build.</p>
            <a className="text-link" href={manifest.repositoryUrl || repositoryUrl}>Build from source <ExternalLink size={14} /></a>
          </div>
        </div>
      ) : null}

      {manifest?.available ? (
        <>
          <div className="release-meta">
            <div><span>Release</span><strong>{manifest.version}</strong></div>
            <div><span>Status</span><strong>Alpha / pre-release</strong></div>
            <div><span>Published</span><strong>{new Date(manifest.publishedAt).toLocaleDateString()}</strong></div>
          </div>
          <div className="download-grid">
            {manifest.targets.map((target) => (
              <article id={target.target === "source" ? "source-code" : undefined} className={`download-card ${target.primary ? "primary" : ""}`} key={target.target}>
                {target.primary
                  ? <span className="card-badge">Windows 11 primary</span>
                  : <span className="card-badge muted">{target.target === "source" ? "Source snapshot" : "Experimental"}</span>}
                <h3>{target.label}</h3>
                <p>{target.requirements}</p>
                <dl>
                  <div><dt>Archive</dt><dd>{target.archive}</dd></div>
                  <div><dt>Size</dt><dd>{formatBytes(target.bytes)}</dd></div>
                  <div><dt>SHA-256</dt><dd><code>{target.sha256.slice(0, 16)}…</code></dd></div>
                </dl>
                <a className="button primary" href={target.archiveUrl}><Download size={18} /> Download {target.label}</a>
                <div className="asset-links">
                  <a href={target.checksumUrl}>Checksum</a>
                  <a href={target.sigstoreUrl}>Sigstore bundle</a>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : null}

      <div id="quick-start" className="instruction-grid">
        <article className="instruction-card">
          <CheckCircle2 aria-hidden="true" />
          <h3>1. Verify before extraction</h3>
          <p>Compare the SHA-256 file, then bind the archive to the Mashr Systems release workflow with Cosign verification.</p>
          <pre><code>sha256sum -c blockr-ARCHIVE.tar.gz.sha256</code></pre>
          <pre><code>{`cosign verify-blob blockr-ARCHIVE.tar.gz \\\n  --bundle blockr-ARCHIVE.tar.gz.sigstore.json \\\n  --certificate-identity-regexp \\\n  '^https://github.com/MASHR-SYSTEM/blockr/.github/workflows/release.yml@refs/tags/v' \\\n  --certificate-oidc-issuer https://token.actions.githubusercontent.com`}</code></pre>
        </article>
        <article className="instruction-card">
          <Terminal aria-hidden="true" />
          <h3>2. Check the installation</h3>
          <p>Extract into an access-controlled directory. Windows builds are not currently Authenticode-signed and may trigger platform warnings.</p>
          <pre><code>{`tar -xf blockr-ARCHIVE.tar.gz
cd blockr-RELEASE
# Windows PowerShell:
.\\blockr.exe doctor
# Linux or macOS:
./blockr doctor`}</code></pre>
        </article>
        <article className="instruction-card wide">
          <Terminal aria-hidden="true" />
          <h3>3. Start with an existing image</h3>
          <p>Analysis is the safer first workflow. Keep acquisition and other privileged operations separate until you have reviewed the operator guide.</p>
          <pre><code>{`blockr case init ./case-001 --label "IR-001" --operator analyst
blockr analyze all ./disk.raw --case ./case-001 --evidence-id EVIDENCE_UUID
blockr export report --case ./case-001 --output ./report.html --format html
blockr case finalize ./case-001
blockr case verify ./case-001`}</code></pre>
          <a className="text-link" href={`${repositoryUrl}/blob/main/docs/operator-guide.md`}>Read the complete operator guide <ExternalLink size={14} /></a>
        </article>
      </div>
    </section>
  );
}
